<script lang="ts">
import type { TIconValue } from '@/utils/helper-objects'
import { type PropType, defineComponent, useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import CarouselHeader from './CarouselHeader.vue'
import CarouselPagination from './CarouselPagination.vue'
import CarouselScrollBox, {
  type ScrollboxInitializedEvent,
} from './CarouselScrollBox.vue'

export default defineComponent({
  components: {
    CarouselHeader,
    CarouselPagination,
    CarouselScrollBox,
  },
  props: {
    icon: {
      type: String as PropType<TIconValue> | null,
    },
    title: {
      type: String,
      required: false,
    },
    frameClass: {
      type: String,
      default: '',
    },
    viewAllRoute: {
      type: Object as PropType<RouteLocationRaw>,
    },
    /**
     * The initial index to scroll to when the component is mounted.
     * Note this is different to the visible page which may show
     * multiple cards.
     *
     * @type {number}
     * @default 0
     */
    initialIndex: {
      type: Number,
      default: 0,
    },

    /**
     * Determines if the scrollbox should autoplay. If `true`, the
     * scrollbox will automatically scroll to the next item
     * every 4 seconds.
     */
    autoplay: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines if the scrollbox should autoplay. If `true`, the
     * scrollbox will automatically scroll to the next item
     * every 4 seconds.
     */
    showNavigation: {
      type: Boolean,
      default: true,
    },

    /**
     * The interval in milliseconds between each autoplay scroll.
     * Defaults to `4000` (4 seconds).
     */
    interval: {
      type: Number,
      default: 4000,
    },

    /**
     * The delay in milliseconds before autoplay starts.
     * Defaults to `0` (no delay).
     */
    autoplayDelay: {
      type: Number,
      default: 0,
    },

    /**
     * Whether to show pagination bullets below the carousel.
     */
    pagination: {
      type: Boolean,
      default: false,
    },
    containerClass: {
      type: String,
      default: '',
    },
  },
  emits: ['scrollstart', 'scrollstop'],
  setup() {
    return {
      scrollbox:
        useTemplateRef<InstanceType<typeof CarouselScrollBox>>('scrollbox'),
    }
  },
  data() {
    return {
      scrollboxChildren: [] as HTMLElement[],
    }
  },
  methods: {
    setScrollboxChildren({ children }: ScrollboxInitializedEvent) {
      // If all items are visible, we don't need to paginate
      this.scrollboxChildren =
        (this.scrollbox?.getVisibleIndexes({ includePartial: true })?.length ??
          0) >= children.length
          ? []
          : children
    },
  },
})
</script>

<template>
  <div :class="['flex flex-col gap-3', containerClass]">
    <CarouselHeader
      v-if="showNavigation || viewAllRoute || title"
      :icon
      :title
      :view-all-route="viewAllRoute"
      :show-navigation="showNavigation"
      :next-disabled="scrollbox?.atEnd"
      :previous-disabled="scrollbox?.atStart"
      @next-click="scrollbox?.nextPage()"
      @previous-click="scrollbox?.previousPage()"
    >
      <template #view-all-route>
        <slot name="view-all-route" :view-all-route="viewAllRoute"></slot>
      </template>
    </CarouselHeader>

    <slot name="preamble"></slot>

    <CarouselScrollBox
      ref="scrollbox"
      :initial-index="initialIndex"
      :autoplay="autoplay"
      :interval="interval"
      :autoplay-delay="autoplayDelay"
      :class="frameClass"
      @scrollstart="$emit('scrollstart')"
      @scrollstop="$emit('scrollstop')"
      @initialized="setScrollboxChildren"
    >
      <slot></slot>
    </CarouselScrollBox>
    <CarouselPagination
      v-if="pagination && scrollboxChildren.length"
      :elements="scrollboxChildren"
      :current-index="scrollbox?.currentIndex"
      @scroll-to="scrollbox?.scrollTo($event)"
    />
  </div>
</template>
